/**
 * Groq API Service
 * 
 * Handles all interactions with the Groq AI API for construction calculations.
 * 
 * SECURITY: API calls are made through a backend serverless function (/api/chat)
 * to keep the API key secure. The API key is NEVER exposed to the browser.
 */

// API endpoint - uses relative URL for same-origin, or full URL for production
const API_ENDPOINT = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api/chat`
  : '/api/chat';

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Send a message to the AI through the secure backend
 * @param {Array} messages - Array of message objects
 * @param {string} materialType - Type of material for specialized prompts
 * @param {Object} options - Additional options
 * @param {boolean} options.useCheaperModel - Whether to use the cheaper/faster model
 * @returns {Promise<string>} AI response
 */
export const sendMessageToGroq = async (messages, materialType = 'general', options = {}) => {
  const { useCheaperModel = false } = options;

  let lastError;
  
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: messages.map(m => ({
            role: m.role,
            content: m.content
          })),
          materialType,
          useCheaperModel
        }),
      });

      // Handle rate limiting
      if (response.status === 429) {
        const data = await response.json().catch(() => ({}));
        const retryAfter = data.retryAfter || 30;
        
        if (attempt < MAX_RETRIES) {
          await sleep(retryAfter * 1000);
          continue;
        }
        throw new Error('Service is busy. Please wait a moment and try again.');
      }

      // Handle other errors
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Request failed with status ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.response) {
        throw new Error('No response content from AI');
      }

      return data.response;
      
    } catch (error) {
      lastError = error;
      console.error(`API Error (attempt ${attempt}/${MAX_RETRIES}):`, error.message);
      
      // Don't retry on certain errors
      if (error.message.includes('authentication') || error.message.includes('configuration')) {
        throw error;
      }
      
      if (attempt < MAX_RETRIES) {
        await sleep(RETRY_DELAY * attempt);
      }
    }
  }

  // All retries failed
  throw lastError || new Error('Failed to get response after multiple attempts');
};

/**
 * Get the system prompt for a material type
 * Note: This is now handled server-side, but kept for reference/testing
 */
export const getMaterialSystemPrompt = (materialType) => {
  const materialDescriptions = {
    general: 'General construction calculations',
    concrete: 'Concrete and masonry calculations',
    drywall: 'Drywall and sheetrock calculations',
    paint: 'Paint and primer calculations',
    flooring: 'Flooring material calculations',
    lumber: 'Lumber and framing calculations',
    roofing: 'Roofing material calculations',
  };
  
  return materialDescriptions[materialType] || materialDescriptions.general;
};

/**
 * Validate if a response is construction-related (for quality control)
 */
export const isConstructionRelated = (text) => {
  const constructionKeywords = [
    'concrete', 'drywall', 'paint', 'flooring', 'lumber', 'roofing',
    'square feet', 'sq ft', 'cubic yard', 'gallon', 'sheet', 'bundle',
    'nail', 'screw', 'stud', 'joist', 'rafter', 'shingle', 'tile',
    'plywood', 'cement', 'mortar', 'grout', 'insulation', 'siding',
    'deck', 'fence', 'wall', 'ceiling', 'floor', 'foundation',
    'materials', 'construction', 'building', 'install', 'measure'
  ];
  
  const lowerText = text.toLowerCase();
  return constructionKeywords.some(keyword => lowerText.includes(keyword));
};
